/**
 * Paginas publicas de senha, no mesmo visual do login:
 *   /recuperar          Esqueci minha senha: pede o link por e-mail;
 *   /recuperar/<token>  define a senha nova (link do e-mail, 30 min, ou 72 h
 *                       quando a equipe manda pelo portal);
 *   /convite/<token>    cria a senha de quem foi convidado (equipe ou portal).
 *
 * O token so vive na URL desta pagina e no corpo do POST: nao vai para
 * localStorage, log nem mensagem de erro. Servem a equipe e o cliente do
 * portal: o servidor decide o que o convite cria.
 */
import { useState, type ReactNode } from 'react';
import { useIdioma, type Chave } from '../i18n/index.tsx';
import { tokenDoCaminho } from './token-caminho.ts';
import './login.css';


function Moldura({ titulo, texto, children }: { titulo: Chave; texto: Chave; children: ReactNode }) {
  const { t } = useIdioma();
  return <div className="login">
    <aside className="login__marca">
      <div className="login__marca-topo">
        <img className="login__logotipo" src="/marca/logo-tme-horizontal.png"
          alt="Terapia Manual Evolutiva TME" />
      </div>
      <p className="login__frase">{t('login.marca.frase')}</p>
      <span className="kicker login__dominio">crm.tmebrasil.com.br</span>
    </aside>
    <main className="login__painel">
      <div className="login__form">
        <span className="kicker login__kicker">{t('app.nome')}</span>
        <h1 className="login__titulo">{t(titulo)}</h1>
        <p className="login__subtitulo">{t(texto)}</p>
        {children}
        <a className="login__esqueci" href="/login">{t('senha.voltar')}</a>
      </div>
    </main>
  </div>;
}

export function PedirRecuperacao() {
  const { t } = useIdioma();
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  return <Moldura titulo="senha.esqueci.titulo" texto="senha.esqueci.texto">
    {enviado
      ? <p className="login__aviso" role="status">{t('senha.esqueci.enviado')}</p>
      : <form noValidate={false} onSubmit={(e) => {
        e.preventDefault();
        const email = String(new FormData(e.currentTarget).get('email') ?? '').trim();
        setEnviando(true);
        // a resposta e a mesma exista ou nao o e-mail: nao ha o que conferir
        void fetch('/api/v1/auth/recuperar', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        }).catch(() => null).finally(() => {
          setEnviando(false);
          setEnviado(true);
        });
      }}>
        <label className="campo">
          <span className="campo__rotulo">{t('login.email')}</span>
          <input className="campo__entrada" type="email" name="email" autoComplete="email"
            required autoFocus />
        </label>
        <button className="botao botao--primario" type="submit" disabled={enviando}>
          {t('senha.esqueci.enviar')}</button>
      </form>}
  </Moldura>;
}

/** Criar senha pelo convite ou definir senha nova pelo link de recuperacao. */
export function DefinirSenha({ modo, token }: { modo: 'convite' | 'recuperar'; token: string | null }) {
  const { t } = useIdioma();
  const [erro, setErro] = useState<Chave | null>(token === null ? 'senha.erro.link' : null);
  const [pronto, setPronto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const titulo = modo === 'convite' ? 'senha.convite.titulo' : 'senha.nova.titulo';
  const texto = modo === 'convite' ? 'senha.convite.texto' : 'senha.nova.texto';
  if (pronto) {
    return <Moldura titulo={titulo} texto={texto}>
      <p className="login__aviso" role="status">{t('senha.pronto')}</p>
      <a className="botao botao--primario login__botao-link" href="/login">{t('senha.entrar')}</a>
    </Moldura>;
  }
  return <Moldura titulo={titulo} texto={texto}>
    <form onSubmit={(e) => {
      e.preventDefault();
      const d = new FormData(e.currentTarget);
      const senha = String(d.get('senha') ?? '');
      setErro(null);
      if (token === null) { setErro('senha.erro.link'); return; }
      if (senha.length < 12) { setErro('senha.erro.curta'); return; }
      if (senha !== String(d.get('confirmar') ?? '')) { setErro('senha.erro.diferentes'); return; }
      setEnviando(true);
      void fetch(`/api/v1/auth/${modo}/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senha }),
      }).then(async (r) => {
        if (r.ok) { setPronto(true); return; }
        const corpo = await r.json().catch(() => ({})) as { code?: string };
        setErro(corpo.code === 'senha_curta' ? 'senha.erro.curta'
          : r.status === 410 ? 'senha.erro.link' : 'senha.erro.generico');
      }).catch(() => setErro('senha.erro.generico')).finally(() => setEnviando(false));
    }}>
      <label className="campo">
        <span className="campo__rotulo">{t('senha.campo')}</span>
        <input className="campo__entrada" type="password" name="senha" autoComplete="new-password"
          required minLength={12} maxLength={200} autoFocus aria-describedby="senha-regra" />
        <span id="senha-regra" className="campo__rotulo">{t('senha.regra')}</span>
      </label>
      <label className="campo">
        <span className="campo__rotulo">{t('senha.confirmar')}</span>
        <input className="campo__entrada" type="password" name="confirmar"
          autoComplete="new-password" required minLength={12} maxLength={200} />
      </label>
      {erro !== null && <p className="login__erro" role="alert">{t(erro)}</p>}
      <button className="botao botao--primario" type="submit" disabled={enviando || token === null}>
        {t('senha.salvar')}</button>
    </form>
  </Moldura>;
}

/**
 * A pagina publica para este caminho, ou null. Vale com ou sem sessao: quem
 * clica no link do e-mail pode estar logado com outra conta no navegador.
 */
export function paginaPublica(caminho: string): ReactNode | null {
  if (caminho === '/recuperar' || caminho === '/recuperar/') return <PedirRecuperacao />;
  if (caminho.startsWith('/recuperar/')) {
    return <DefinirSenha modo="recuperar" token={tokenDoCaminho(caminho, '/recuperar/')} />;
  }
  if (caminho.startsWith('/convite/')) {
    return <DefinirSenha modo="convite" token={tokenDoCaminho(caminho, '/convite/')} />;
  }
  return null;
}
